/**
 * VoiceRibbon — the signature element (§13).
 *
 * A horizontal monochrome waveform rendered on canvas. It doubles as the
 * turn-state indicator:
 *   - caller speech renders as bright bars   (#F5F5F7)
 *   - agent speech renders as muted bars     (#6E6E76)
 *   - silence is a flat hairline
 *   - "thinking" is a subtle centre pulse
 *
 * Modes:
 *   - Live   — pass `liveLevel` (0..1, e.g. from useMicLevel) and `state`;
 *              the ribbon scrolls right-to-left and keeps ~10s of history, so
 *              barge-in and turn detection are literally visible.
 *   - Static — pass `samples` (0..1) to render a fixed waveform (call detail
 *              scrubbing); `sampleStates` colours per-sample by speaker and
 *              `progress` (0..1) draws a playhead.
 *   - Idle   — `state="idle"` with no samples: a slow, quiet drift used on
 *              the login/register screens.
 *
 * Respects prefers-reduced-motion: with `reducedMotionAware` (default true)
 * all animation collapses to a calm static rendering.
 */

import { useEffect, useRef, useState } from "react";

import { cx } from "../lib/format";

export type RibbonState = "idle" | "user" | "agent" | "thinking";

export interface VoiceRibbonProps {
  /** Fixed waveform, values 0..1 — static mode (e.g. recorded call). */
  samples?: number[];
  /** Speaker per sample for static mode; defaults to "agent" (muted). */
  sampleStates?: Array<"user" | "agent" | "silence">;
  /** Playhead position 0..1 for static mode. */
  progress?: number;
  /** Live input level 0..1 (from useMicLevel). Used in live mode. */
  liveLevel?: number;
  /** Whose turn it is — drives colour and the thinking pulse. */
  state: RibbonState;
  /** Collapse all animation under prefers-reduced-motion. Default true. */
  reducedMotionAware?: boolean;
  /** Canvas height in CSS pixels. Default 80. */
  height?: number;
  className?: string;
  "aria-label"?: string;
}

interface Bar {
  amp: number; // 0..1
  kind: "user" | "agent" | "silence";
}

const BAR_WIDTH = 2;
const BAR_GAP = 1;
const BAR_STEP = BAR_WIDTH + BAR_GAP;
const SAMPLE_INTERVAL_MS = 33; // ~30 bars/sec of history

const FALLBACK = {
  bright: "#f5f5f7",
  muted: "#6e6e76",
  line: "#2a2a2e",
};

function readColors(el: HTMLElement): typeof FALLBACK {
  const style = getComputedStyle(el);
  const read = (name: string, fallback: string) =>
    style.getPropertyValue(name).trim() || fallback;
  return {
    bright: read("--bright-hex", FALLBACK.bright),
    muted: read("--muted-hex", FALLBACK.muted),
    line: read("--line-hex", FALLBACK.line),
  };
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Smooth speech-like amplitude used when the agent speaks but no level is fed in. */
function synthSpeech(t: number): number {
  const gate = Math.sin(t * 0.9) > -0.35 ? 1 : 0.1; // conversational pauses
  const raw =
    0.4 +
    0.28 * Math.sin(t * 7.3) +
    0.18 * Math.sin(t * 13.7 + 1.3) +
    0.14 * Math.sin(t * 3.1 + 0.5);
  return clamp01(raw * gate);
}

export function VoiceRibbon({
  samples,
  sampleStates,
  progress,
  liveLevel,
  state,
  reducedMotionAware = true,
  height = 80,
  className,
  "aria-label": ariaLabel,
}: VoiceRibbonProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Refs so the animation loop reads fresh values without restarting, and so
  // the bar history survives effect re-runs (turn changes must NOT wipe it —
  // seeing barge-in in the history is the point of the ribbon).
  const stateRef = useRef<RibbonState>(state);
  const levelRef = useRef<number | undefined>(liveLevel);
  const samplesRef = useRef<number[] | undefined>(samples);
  const sampleStatesRef = useRef<VoiceRibbonProps["sampleStates"]>(sampleStates);
  const progressRef = useRef<number | undefined>(progress);
  const barsRef = useRef<Bar[]>([]);
  const drawRef = useRef<((now: number) => void) | null>(null);
  stateRef.current = state;
  levelRef.current = liveLevel;
  samplesRef.current = samples;
  sampleStatesRef.current = sampleStates;
  progressRef.current = progress;

  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    if (!reducedMotionAware) {
      setReducedMotion(false);
      return;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(query.matches);
    const onChange = (event: MediaQueryListEvent) => setReducedMotion(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, [reducedMotionAware]);

  const staticMode = samples !== undefined;

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const colors = readColors(container);
    let width = 0;
    let raf = 0;
    let lastSampleAt = 0;
    let disposed = false;

    function resize() {
      if (!canvas || !container) return;
      const dpr = window.devicePixelRatio || 1;
      width = container.clientWidth;
      canvas.width = Math.max(1, Math.round(width * dpr));
      canvas.height = Math.max(1, Math.round(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function colorFor(kind: Bar["kind"]): string {
      if (kind === "user") return colors.bright;
      if (kind === "agent") return colors.muted;
      return colors.line;
    }

    function drawBar(x: number, amp: number, color: string, alpha: number) {
      if (!ctx) return;
      const mid = height / 2;
      const maxAmp = height / 2 - 4;
      const h = Math.max(1, amp * maxAmp * 2);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = color;
      ctx.fillRect(x, mid - h / 2, BAR_WIDTH, h);
    }

    function drawHairline(alpha: number) {
      if (!ctx) return;
      ctx.globalAlpha = alpha;
      ctx.fillStyle = colors.line;
      ctx.fillRect(0, height / 2 - 0.5, width, 1);
    }

    /** Static mode — a fixed recorded waveform with optional playhead. */
    function drawStatic() {
      if (!ctx) return;
      const data = samplesRef.current ?? [];
      const kinds = sampleStatesRef.current;
      const played = progressRef.current;
      ctx.clearRect(0, 0, width, height);
      drawHairline(0.6);

      const count = Math.max(1, Math.floor(width / BAR_STEP));
      for (let i = 0; i < count; i++) {
        const u = i / count;
        const sourceIndex = Math.min(Math.max(0, data.length - 1), Math.floor(u * data.length));
        const amp = clamp01(data[sourceIndex] ?? 0);
        const kind: Bar["kind"] = kinds?.[sourceIndex] ?? (amp < 0.02 ? "silence" : "agent");
        const beyondPlayhead = played !== undefined && u > played;
        drawBar(i * BAR_STEP, amp, colorFor(kind), beyondPlayhead ? 0.35 : 1);
      }
      if (played !== undefined) {
        ctx.globalAlpha = 0.9;
        ctx.fillStyle = colors.bright;
        ctx.fillRect(Math.min(width - 1, played * width), 2, 1, height - 4);
      }
      ctx.globalAlpha = 1;
    }

    /** Idle mode — slow, quiet drift (login screens). */
    function drawIdle(t: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      const count = Math.max(1, Math.floor(width / BAR_STEP));
      for (let i = 0; i < count; i++) {
        const u = (i + 0.5) / count;
        const envelope = Math.pow(Math.sin(Math.PI * u), 0.9);
        const x = i * BAR_STEP;
        const wave =
          0.5 * Math.sin(x * 0.045 + t * 0.9) +
          0.35 * Math.sin(x * 0.012 - t * 0.35) +
          0.15 * Math.sin(x * 0.08 + t * 1.7);
        const amp = Math.max(0, wave) * envelope * 0.4 + 0.015;
        drawBar(x, amp, colors.muted, Math.min(0.9, 0.35 + amp * 1.4));
      }
      ctx.globalAlpha = 1;
    }

    /** Live mode — scrolling history of turn-coloured bars. */
    function drawLive(t: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      drawHairline(0.6);

      const bars = barsRef.current;
      const capacity = Math.max(1, Math.floor(width / BAR_STEP));
      const start = Math.max(0, bars.length - capacity);
      const visible = bars.length - start;
      for (let i = 0; i < visible; i++) {
        const bar = bars[start + i];
        // Newest bar sits at the right edge; history scrolls left.
        const x = width - (visible - i) * BAR_STEP;
        // Fade toward the left edge so history dissolves instead of clipping.
        const edgeFade = Math.min(1, (x / Math.max(1, width)) * 6 + 0.08);
        if (bar.kind === "silence" || bar.amp < 0.02) {
          drawBar(x, 0, colors.line, 0.8 * edgeFade);
        } else {
          drawBar(x, bar.amp, colorFor(bar.kind), edgeFade);
        }
      }

      // Thinking: a subtle breathing swell at the leading edge.
      if (stateRef.current === "thinking") {
        const pulse = 0.5 + 0.5 * Math.sin(t * 2 * Math.PI * 1.1);
        const swellWidth = Math.min(160, width * 0.25);
        const startX = width - swellWidth;
        for (let x = startX; x < width; x += BAR_STEP) {
          const u = (x - startX) / swellWidth;
          const envelope = Math.pow(Math.sin(Math.PI * u), 2);
          drawBar(x, 0.05 + 0.07 * pulse * envelope, colors.muted, 0.35 + 0.3 * pulse);
        }
      }
      ctx.globalAlpha = 1;
    }

    /** Reduced motion — one calm static frame per prop change, no loop. */
    function drawReduced() {
      if (!ctx) return;
      ctx.clearRect(0, 0, width, height);
      const current = stateRef.current;
      if (current === "user" || current === "agent") {
        drawHairline(0.6);
        // A centred block whose height mirrors the current level.
        const amp = clamp01(levelRef.current ?? 0.4);
        const color = current === "user" ? colors.bright : colors.muted;
        const blockWidth = Math.min(240, width * 0.4);
        const startX = (width - blockWidth) / 2;
        for (let x = startX; x < startX + blockWidth; x += BAR_STEP) {
          const u = (x - startX) / blockWidth;
          const envelope = Math.pow(Math.sin(Math.PI * u), 0.7);
          drawBar(x, amp * envelope, color, 0.9);
        }
      } else {
        drawHairline(current === "thinking" ? 0.9 : 0.6);
      }
      ctx.globalAlpha = 1;
    }

    function drawFrame(now: number) {
      if (samplesRef.current) {
        drawStatic();
      } else if (reducedMotion) {
        drawReduced();
      } else if (stateRef.current === "idle" && barsRef.current.length === 0) {
        drawIdle(now / 1000);
      } else {
        drawLive(now / 1000);
      }
    }
    drawRef.current = drawFrame;

    const observer = new ResizeObserver(() => {
      resize();
      drawFrame(performance.now());
    });
    observer.observe(container);
    resize();
    drawFrame(performance.now());

    if (staticMode || reducedMotion) {
      // No animation loop; redraws happen via the effect below on prop change.
      return () => {
        observer.disconnect();
        drawRef.current = null;
      };
    }

    function pushSample(now: number) {
      const current = stateRef.current;
      const level = levelRef.current;
      const t = now / 1000;
      let bar: Bar;
      if (current === "user") {
        bar = { amp: clamp01(level ?? 0), kind: "user" };
      } else if (current === "agent") {
        bar = { amp: clamp01(level ?? synthSpeech(t)), kind: "agent" };
      } else {
        bar = { amp: 0, kind: "silence" };
      }
      const bars = barsRef.current;
      bars.push(bar);
      const cap = Math.max(64, Math.floor(width / BAR_STEP) + 8);
      if (bars.length > cap) bars.splice(0, bars.length - cap);
    }

    function loop(now: number) {
      if (disposed) return;
      if (now - lastSampleAt >= SAMPLE_INTERVAL_MS) {
        // Push in idle only once speech has ever happened (keeps pure-idle
        // screens on the drifting animation instead of an empty scroller).
        if (stateRef.current !== "idle" || barsRef.current.length > 0) {
          let steps = Math.min(4, Math.floor((now - lastSampleAt) / SAMPLE_INTERVAL_MS));
          while (steps-- > 0) pushSample(now);
        }
        lastSampleAt = now;
      }
      drawFrame(now);
      raf = requestAnimationFrame(loop);
    }
    lastSampleAt = performance.now();
    raf = requestAnimationFrame(loop);

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      observer.disconnect();
      drawRef.current = null;
    };
  }, [height, reducedMotion, staticMode]);

  // Static + reduced-motion modes redraw on data/state changes (no loop).
  useEffect(() => {
    if (staticMode || reducedMotion) {
      drawRef.current?.(performance.now());
    }
  }, [staticMode, reducedMotion, samples, sampleStates, progress, state, liveLevel]);

  return (
    <div
      ref={containerRef}
      className={cx("w-full", className)}
      role="img"
      aria-label={ariaLabel ?? ribbonLabel(state)}
    >
      <canvas ref={canvasRef} className="block" />
    </div>
  );
}

function ribbonLabel(state: RibbonState): string {
  switch (state) {
    case "user":
      return "You are speaking";
    case "agent":
      return "Agent is speaking";
    case "thinking":
      return "Agent is thinking";
    case "idle":
      return "Waveform idle";
  }
}

// ---------------------------------------------------------------------------
// useMicLevel — RMS level (0..1) from a MediaStream, smoothed for the ribbon
// ---------------------------------------------------------------------------

/**
 * Returns a smoothed 0..1 level for any audio MediaStream (mic or remote
 * agent audio). Re-renders are throttled to visible changes.
 */
export function useMicLevel(stream: MediaStream | null): number {
  const [level, setLevel] = useState(0);
  const lastRef = useRef(0);

  useEffect(() => {
    if (!stream || stream.getAudioTracks().length === 0) {
      lastRef.current = 0;
      setLevel(0);
      return;
    }

    type AudioContextCtor = new () => AudioContext;
    const Ctor: AudioContextCtor | undefined =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextCtor }).webkitAudioContext;
    if (!Ctor) return;

    const audioContext = new Ctor();
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 512;
    analyser.smoothingTimeConstant = 0.5;
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    let smoothed = 0;
    let raf = 0;

    function tick() {
      analyser.getByteTimeDomainData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / data.length);
      // Speech RMS rarely exceeds ~0.3 — boost into a usable visual range.
      const boosted = Math.min(1, rms * 3.5);
      // Fast attack, slower release — feels like a meter, not a strobe.
      smoothed = boosted > smoothed ? smoothed * 0.4 + boosted * 0.6 : smoothed * 0.82;
      const snapped = smoothed < 0.015 ? 0 : smoothed;
      if (Math.abs(snapped - lastRef.current) > 0.01) {
        lastRef.current = snapped;
        setLevel(snapped);
      }
      raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      source.disconnect();
      analyser.disconnect();
      void audioContext.close().catch(() => undefined);
    };
  }, [stream]);

  return level;
}
